// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import type User from "le-coffre-resources/dist/SuperAdmin";
import type { QueryPayload } from "../../../types";
import type { IGetUsersparams, IPutUsersParams } from "./Users";
import { buildUsersUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface UsersInstance {
	get(q: IGetUsersparams): Promise<User[]>;
	getByUid(uid: string, q?: QueryPayload): Promise<User>;
	put(uid: string, body: IPutUsersParams): Promise<User>;
}

// Module-level singleton instance
let instance: UsersInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

function createUsers(): UsersInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}

	const baseSuperAdmin = baseSuperAdminInstance;
	const baseUrl = baseSuperAdmin.namespaceUrl.concat("/users");

	return {
		get: async (q: IGetUsersparams): Promise<User[]> => {
			const url = new URL(baseUrl);
			baseSuperAdmin.appendJsonSearchParams(url, { q });
			try {
				return await baseSuperAdmin.getRequest<User[]>(url);
			} catch (err) {
				baseSuperAdmin.onError(err);
				return Promise.reject(err);
			}
		},
		getByUid: async (uid: string, q?: QueryPayload): Promise<User> => {
			const url = new URL(baseUrl.concat(`/${uid}`));
			if (q) baseSuperAdmin.appendJsonSearchParams(url, q);
			try {
				return await baseSuperAdmin.getRequest<User>(url);
			} catch (err) {
				baseSuperAdmin.onError(err);
				return Promise.reject(err);
			}
		},
		put: async (uid: string, body: IPutUsersParams): Promise<User> => {
			const url = new URL(baseUrl.concat(`/${uid}`));
			try {
				const payload = buildUsersUpdatePayload(body);
				return await baseSuperAdmin.putRequest<User>(url, payload);
			} catch (err) {
				baseSuperAdmin.onError(err);
				return Promise.reject(err);
			}
		},
	};
}

export function getUsersInstance(): UsersInstance {
	if (!instance) {
		instance = createUsers();
	}
	return instance;
}
